import {
  BadgeCheck,
  Banknote,
  BookOpenCheck,
  ClipboardCheck,
  FileSignature,
  HandCoins,
  KeyRound,
  Landmark,
  ReceiptText,
  RotateCcw,
  ShieldCheck,
  WalletCards,
  type LucideIcon,
} from 'lucide-react'
import { pointerGlow, staggerChildren } from '../animations/gsapAnimations'
import Button from '../components/ui/Button'
import PageIntro from '../components/ui/PageIntro'
import Section from '../components/ui/Section'
import SectionHeading from '../components/ui/SectionHeading'
import { useGsapContext } from '../hooks/useGsapReveal'

type PolicyBlock =
  | { kind: 'paragraph'; text: string }
  | { kind: 'list'; items: readonly string[] }
  | { kind: 'callout'; text: string }

type PolicyClause = {
  number: number
  title: string
  blocks: readonly PolicyBlock[]
}

type PolicyGroup = {
  id: string
  range: string
  eyebrow: string
  title: string
  icon: LucideIcon
  clauses: readonly PolicyClause[]
}

/* The legal copy remains fully visible rather than being folded into
   accordions. A policy is something a customer should be able to scan,
   search and print without first guessing which heading contains the term
   that matters to them. */
const POLICY_GROUPS = [
  {
    id: 'booking-delivery',
    range: '01—03',
    eyebrow: 'Booking, delivery & documents',
    title: 'From booking through post-delivery documents',
    icon: KeyRound,
    clauses: [
      {
        number: 1,
        title: 'Booking / advance payment',
        blocks: [
          {
            kind: 'paragraph',
            text: 'A Booking / Advance Payment of Rs. 50,000 shall be required for each vehicle at the time of booking.',
          },
          {
            kind: 'paragraph',
            text: 'The Rs. 50,000 Booking / Advance Payment shall be Non-Refundable once the booking process has been initiated, as Sultan Motors proceeds with the transaction and may make payments, commitments, or arrangements with the concerned Bank / Leasing Company or other relevant parties on behalf of the customer.',
          },
          {
            kind: 'paragraph',
            text: 'If the transaction proceeds to completion, the Rs. 50,000 booking amount shall be considered part of the total amount payable by the customer.',
          },
          {
            kind: 'paragraph',
            text: 'The vehicle price, Down Payment, Monthly Instalment, and Plan Duration shall be according to the relevant written agreement.',
          },
        ],
      },
      {
        number: 2,
        title: 'Vehicle delivery & complete payment',
        blocks: [
          {
            kind: 'paragraph',
            text: 'At the time of vehicle delivery, the customer shall pay the complete remaining amount due according to the relevant Sale Agreement / Payment Plan.',
          },
          {
            kind: 'paragraph',
            text: 'Sultan Motors shall not be obligated to hand over or deliver the vehicle until the complete amount due at the time of delivery has been received and confirmed.',
          },
          {
            kind: 'paragraph',
            text: 'The vehicle shall only be handed over after confirmation of the required payment and completion of all necessary documentation.',
          },
        ],
      },
      {
        number: 3,
        title: 'Post-delivery documents / registration',
        blocks: [
          {
            kind: 'paragraph',
            text: 'Sultan Motors shall provide the customer with the following documents within 15 days after delivery of the vehicle:',
          },
          {
            kind: 'list',
            items: [
              'Car Driving Authority Letter',
              'Excise Receipt',
              'Bank Instalment / Payment Schedule',
            ],
          },
          {
            kind: 'paragraph',
            text: 'The Original Number Plates and Original Smart Card shall be provided to the customer after completion of the required Vehicle Verification and according to the applicable processing / issuance schedule of the Islamabad Excise & Taxation Department.',
          },
          {
            kind: 'paragraph',
            text: 'Any delay in issuance of the Original Number Plates or Original Smart Card by the concerned Excise Authority shall remain subject to the official processing schedule of the relevant department.',
          },
        ],
      },
    ],
  },
  {
    id: 'instalments-recovery',
    range: '04—08',
    eyebrow: 'Bank, instalments & claims',
    title: 'Responsibilities and support after delivery',
    icon: Landmark,
    clauses: [
      {
        number: 4,
        title: 'Bank / leasing terms',
        blocks: [
          {
            kind: 'paragraph',
            text: 'All Bank Leased / Financed vehicles shall remain subject to the approval, terms, and conditions of the concerned Bank / Leasing Company.',
          },
          {
            kind: 'paragraph',
            text: 'Any changes in Bank Markup, Insurance, Registration, Taxes, Government Charges, or Bank Charges shall be applicable accordingly.',
          },
        ],
      },
      {
        number: 5,
        title: 'Monthly instalment responsibility',
        blocks: [
          {
            kind: 'paragraph',
            text: 'After delivery of the vehicle, all Monthly Bank Instalments must be paid in full and on time by the customer, unless otherwise specifically agreed in writing.',
          },
        ],
      },
      {
        number: 6,
        title: 'Late / short / unpaid instalments',
        blocks: [
          {
            kind: 'paragraph',
            text: 'If any instalment is paid late, remains unpaid, is short, or becomes overdue, all resulting Late Payment Charges, Penalties, and other related Bank Charges shall be the customer’s responsibility.',
          },
        ],
      },
      {
        number: 7,
        title: 'Bank repossession / recovery',
        blocks: [
          {
            kind: 'paragraph',
            text: 'If the Bank / Leasing Company repossesses or recovers the vehicle due to non-payment, short payment, overdue instalments, or any default by the customer, the entire responsibility shall rest with the customer.',
          },
          {
            kind: 'paragraph',
            text: 'All amounts required for release / recovery of the vehicle, including:',
          },
          {
            kind: 'list',
            items: [
              'Pay-Off Amount',
              'Outstanding Instalments',
              'Recovery / Repossession Charges',
              'Late Payment Charges',
              'Penalties',
              'Other Related Bank Charges',
            ],
          },
          {
            kind: 'paragraph',
            text: 'shall be the sole responsibility of the customer.',
          },
        ],
      },
      {
        number: 8,
        title: 'Accident / insurance claim assistance',
        blocks: [
          {
            kind: 'paragraph',
            text: 'In case of an accident involving the vehicle, Sultan Motors shall assist the customer in initiating and processing the applicable Insurance / Bank Claim.',
          },
          {
            kind: 'paragraph',
            text: 'Sultan Motors shall provide or facilitate the relevant available documents required from its side / concerned Bank so that the customer’s claim may be submitted and processed.',
          },
          {
            kind: 'paragraph',
            text: 'The final approval, Claim Amount, Survey, Repair Authorisation, Processing Time, Deductions, or Rejection of any claim shall remain subject to the concerned Insurance Company / Bank / Leasing Company, applicable Insurance Policy terms, and completion of the required documentation.',
          },
          {
            kind: 'paragraph',
            text: 'Sultan Motors shall provide reasonable assistance throughout the claim process but shall not be responsible for any delay or rejection by the concerned Bank or Insurance Company.',
          },
        ],
      },
    ],
  },
  {
    id: 'cancellation-resale',
    range: '09—11',
    eyebrow: 'Transfer, return & resale',
    title: 'When ownership plans change',
    icon: RotateCcw,
    clauses: [
      {
        number: 9,
        title: 'Restriction on sale / transfer & resale',
        blocks: [
          {
            kind: 'paragraph',
            text: 'If the customer intends to sell the vehicle, the customer must first contact Sultan Motors, and the vehicle shall be parked / placed at the Sultan Motors floor for resale.',
          },
          {
            kind: 'paragraph',
            text: 'Until the vehicle is actually sold, all Monthly Bank Instalments shall remain the sole responsibility of the customer and must be paid in full and on time.',
          },
          {
            kind: 'paragraph',
            text: 'Parking or handing over the vehicle to Sultan Motors for resale shall not transfer responsibility for the Bank Instalments to Sultan Motors.',
          },
          {
            kind: 'paragraph',
            text: 'The customer shall not sell, transfer, dispose of, or hand over the vehicle to any third party without first contacting Sultan Motors and obtaining the required clearance / consent.',
          },
          {
            kind: 'paragraph',
            text: 'Any unauthorised sale, transfer, disposal, or handing over of the vehicle shall constitute a breach of the agreed terms.',
          },
          {
            kind: 'paragraph',
            text: 'In such circumstances, Sultan Motors may take appropriate lawful steps for recovery / possession of the vehicle, subject to the relevant signed agreement, applicable Bank / Leasing Company terms, and applicable law.',
          },
        ],
      },
      {
        number: 10,
        title: 'Cancellation / vehicle return & 30-day resale process',
        blocks: [
          {
            kind: 'paragraph',
            text: 'If the customer wishes to return the vehicle, the customer shall park / hand over the vehicle at the Sultan Motors floor for resale.',
          },
          {
            kind: 'paragraph',
            text: 'Sultan Motors shall make reasonable efforts to arrange the resale of the vehicle within 30 days, subject to prevailing market conditions and buyer availability.',
          },
          {
            kind: 'paragraph',
            text: 'After the vehicle is actually sold and the sale proceeds are received, Sultan Motors shall calculate the customer’s Final Settlement and pay the customer’s net payable amount in cash after all applicable deductions, liabilities, charges, and adjustments.',
          },
          {
            kind: 'paragraph',
            text: 'Until the vehicle is actually sold, all Monthly Bank Instalments shall remain the responsibility of the customer and must be paid in full and on time.',
          },
          {
            kind: 'paragraph',
            text: 'Parking or handing over the vehicle at Sultan Motors shall not itself constitute a Refund or Final Settlement.',
          },
          {
            kind: 'callout',
            text: 'A separate “Vehicle Receiving for Resale & Conditional Settlement” shall be prepared and signed by both parties when the vehicle is received for resale.',
          },
        ],
      },
      {
        number: 11,
        title: 'Instalments & down payment on vehicle return',
        blocks: [
          {
            kind: 'paragraph',
            text: 'If the customer returns the vehicle, all Bank Instalments already paid / deposited by the customer shall be Non-Refundable and shall be treated as Rent / Charges for the customer’s use of the vehicle.',
          },
          {
            kind: 'paragraph',
            text: 'However, the Down Payment paid by the customer shall be returned / settled according to the condition of the vehicle and the applicable agreement / adjustments.',
          },
        ],
      },
    ],
  },
  {
    id: 'settlement-deductions',
    range: '12—16',
    eyebrow: 'Settlement & deductions',
    title: 'How the final amount is worked out',
    icon: HandCoins,
    clauses: [
      {
        number: 12,
        title: 'Cancellation charges',
        blocks: [
          {
            kind: 'paragraph',
            text: 'If a completed deal is cancelled at the customer’s request and Sultan Motors agrees to receive the vehicle for resale, 10% of the agreed vehicle value shall be charged / adjusted as Cancellation Charges.',
          },
          {
            kind: 'paragraph',
            text: 'If a different rate is specifically mentioned in the relevant written and signed agreement, that rate shall apply.',
          },
        ],
      },
      {
        number: 13,
        title: 'Commission & other expenses',
        blocks: [
          {
            kind: 'paragraph',
            text: 'Any commission already paid by Sultan Motors in connection with the original sale, along with Documentation Charges or other documented and agreed expenses, may be deducted / adjusted at the time of Final Settlement in accordance with the relevant agreement.',
          },
        ],
      },
      {
        number: 14,
        title: 'Resale price',
        blocks: [
          {
            kind: 'paragraph',
            text: 'A returned vehicle shall be offered for resale according to prevailing market conditions.',
          },
          {
            kind: 'paragraph',
            text: 'The vehicle may be sold for an amount lower than, equal to, or higher than its previous / agreed value.',
          },
          {
            kind: 'paragraph',
            text: 'The Final Settlement shall be based on the:',
          },
          {
            kind: 'callout',
            text: 'ACTUAL RESALE PRICE / ACTUAL AMOUNT RECEIVED.',
          },
        ],
      },
      {
        number: 15,
        title: 'No immediate refund',
        blocks: [
          {
            kind: 'paragraph',
            text: 'Handing over a vehicle to Sultan Motors for resale shall not constitute an Immediate Refund, Buy-Back, Cancellation Settlement, or Full & Final Settlement.',
          },
          {
            kind: 'paragraph',
            text: 'Final Settlement shall only be completed after the vehicle has actually been resold and all applicable deductions, liabilities, and adjustments have been calculated.',
          },
        ],
      },
      {
        number: 16,
        title: 'Vehicle condition & documents',
        blocks: [
          {
            kind: 'paragraph',
            text: 'At the time a vehicle is returned for resale, the following may be inspected and recorded:',
          },
          {
            kind: 'list',
            items: [
              'Vehicle Condition',
              'Mileage',
              'Documents',
              'Keys',
              'Accessories',
              'Visible Damage',
            ],
          },
          {
            kind: 'paragraph',
            text: 'Any loss in value or expenses resulting from Accident Damage, missing Documents, missing Keys / Accessories, Repairs, or other vehicle-condition issues may be deducted / adjusted in accordance with the relevant written agreement.',
          },
        ],
      },
    ],
  },
  {
    id: 'agreement-acceptance',
    range: '17—18',
    eyebrow: 'Agreement & acceptance',
    title: 'The written record comes first',
    icon: FileSignature,
    clauses: [
      {
        number: 17,
        title: 'Written agreement prevails',
        blocks: [
          {
            kind: 'paragraph',
            text: 'For each individual vehicle / transaction, the relevant signed Sale Agreement, Delivery Receipt, Vehicle Receiving, Payment Plan, or Other Written Agreement shall serve as the primary record governing the terms of that specific transaction.',
          },
          {
            kind: 'paragraph',
            text: 'Any verbal commitment or understanding should be recorded in writing and signed by both parties to form part of the transaction record.',
          },
        ],
      },
      {
        number: 18,
        title: 'Customer acceptance',
        blocks: [
          {
            kind: 'paragraph',
            text: 'The customer confirms that the applicable Sultan Motors Policy and the terms relating to the specific vehicle / transaction have been read, understood, and accepted before signing.',
          },
        ],
      },
    ],
  },
] as const satisfies readonly PolicyGroup[]

const HIGHLIGHTS = [
  {
    value: 'Rs. 50,000',
    label: 'Booking / advance',
    body: 'Non-refundable once the booking process is initiated; counted towards the total only if the transaction is completed.',
    icon: Banknote,
  },
  {
    value: '15 days',
    label: 'Post-delivery documents',
    body: 'Driving authority letter, excise receipt and bank payment schedule after delivery.',
    icon: ReceiptText,
  },
  {
    value: '30 days',
    label: 'Reasonable resale effort',
    body: 'Subject to prevailing market conditions and buyer availability; instalments remain due until actual sale.',
    icon: RotateCcw,
  },
  {
    value: '10%',
    label: 'Conditional cancellation',
    body: 'For a completed deal cancelled at the customer’s request and accepted for resale, unless the signed agreement sets another rate.',
    icon: HandCoins,
  },
] as const

const CUSTOMER_FIELDS = [
  'Customer name',
  'CNIC no.',
  'Vehicle',
  'Registration no.',
  'Customer signature',
  'Thumb impression',
] as const

const SULTAN_FIELDS = [
  'Authorised person',
  'Signature',
  'Stamp',
  'Date',
  'Place',
] as const

function Policy() {
  const pageRef = useGsapContext<HTMLDivElement>((scope) => {
    staggerChildren(scope, '[data-policy-reveal]', { y: 22, stagger: 0.06 })

    const teardowns = Array.from(
      scope.querySelectorAll<HTMLElement>('[data-policy-glow]'),
    ).map((card) => pointerGlow(card))

    return () => teardowns.forEach((teardown) => teardown?.())
  })

  return (
    <div ref={pageRef}>
      <PageIntro
        eyebrow="Customer policy"
        ghost="Policy"
        title={
          <>
            Sales, booking, instalment, cancellation &amp;{' '}
            <span className="text-lime">vehicle return policy</span>
          </>
        }
        lede="The complete terms for booking, delivery, registration documents, bank instalments, insurance-claim assistance, transfer, return and resale. For each vehicle, its signed transaction documents remain the primary record."
      >
        <div className="flex flex-col gap-3 min-[420px]:flex-row">
          <Button
            href="#policy-details"
            size="lg"
            arrow
            magnetic
            className="w-full min-[420px]:w-auto"
          >
            Read all 18 clauses
          </Button>
          <Button
            to="/contact"
            variant="ghost"
            size="lg"
            className="w-full min-[420px]:w-auto"
          >
            Ask our team
          </Button>
        </div>
      </PageIntro>

      <Section tone="surface" labelledBy="highlights-title">
        <SectionHeading
          id="highlights-title"
          eyebrow="At a glance"
          title="The key terms, before the detail"
          lede="These highlights are a guide only. Read the complete clauses below together with the signed agreement for your vehicle."
        />

        <ul className="mt-12 grid gap-px overflow-hidden rounded-2xl border border-line bg-line sm:grid-cols-2 xl:grid-cols-4">
          {HIGHLIGHTS.map((item) => (
            <li
              key={item.label}
              data-policy-reveal
              className="group relative bg-raised p-7 transition-colors duration-300 hover:bg-surface sm:p-8"
            >
              <span className="icon-ring size-11 transition duration-300 group-hover:bg-lime group-hover:text-void">
                <item.icon aria-hidden="true" strokeWidth={1.7} className="size-4.5" />
              </span>
              <p className="mt-7 font-display text-[clamp(1.35rem,2vw,1.75rem)] leading-tight font-semibold tracking-[-0.03em] text-platinum">
                {item.value}
              </p>
              <p className="mt-2 label-sm text-lime">{item.label}</p>
              <p className="mt-4 text-[13.5px] leading-relaxed text-steel">
                {item.body}
              </p>
            </li>
          ))}
        </ul>
      </Section>

      <Section id="policy-details" tone="ink" labelledBy="policy-title">
        <div className="grid items-start gap-12 lg:grid-cols-[250px_minmax(0,1fr)] lg:gap-16 xl:grid-cols-[280px_minmax(0,1fr)]">
          <aside className="hidden lg:block">
            <nav
              aria-label="Policy contents"
              className="sticky top-28 overflow-hidden rounded-2xl border border-line bg-raised p-3 shadow-card"
            >
              <p className="px-4 pt-3 pb-4 label-sm text-dim">On this page</p>
              <ol className="flex flex-col gap-1">
                {POLICY_GROUPS.map((group) => (
                  <li key={group.id}>
                    <a
                      href={`#${group.id}`}
                      className="group flex items-center gap-3 rounded-xl px-4 py-3 transition-colors duration-200 hover:bg-lime/8"
                    >
                      <span className="text-[11px] font-semibold text-lime tabular-nums">
                        {group.range}
                      </span>
                      <span className="text-[13.5px] leading-snug font-medium text-silver transition-colors group-hover:text-platinum">
                        {group.eyebrow}
                      </span>
                    </a>
                  </li>
                ))}
              </ol>

              <div className="mx-3 mt-3 border-t border-line-soft px-1 pt-5 pb-3">
                <div className="flex items-start gap-3">
                  <ShieldCheck
                    aria-hidden="true"
                    strokeWidth={1.7}
                    className="mt-0.5 size-4 shrink-0 text-lime"
                  />
                  <p className="text-[12px] leading-relaxed text-dim">
                    The signed documents for each transaction remain its primary record.
                  </p>
                </div>
              </div>
            </nav>
          </aside>

          <div className="min-w-0">
            <SectionHeading
              id="policy-title"
              eyebrow="Complete policy"
              title="Five stages. Eighteen clauses."
              lede="Every clause is shown in full. Use the contents to move between stages without losing the written terms around them."
            />

            <nav
              aria-label="Policy contents"
              className="mt-9 flex gap-2 overflow-x-auto pb-2 lg:hidden"
            >
              {POLICY_GROUPS.map((group) => (
                <a
                  key={group.id}
                  href={`#${group.id}`}
                  className="shrink-0 rounded-full border border-line bg-raised px-4 py-2 text-[12.5px] font-medium text-silver transition-colors hover:border-lime/50 hover:text-lime"
                >
                  <span className="mr-2 text-lime tabular-nums">{group.range}</span>
                  {group.eyebrow}
                </a>
              ))}
            </nav>

            <div className="mt-12 flex flex-col gap-20">
              {POLICY_GROUPS.map((group) => (
                <section
                  key={group.id}
                  id={group.id}
                  aria-labelledby={`${group.id}-title`}
                  className="scroll-mt-28"
                >
                  <div data-policy-reveal className="flex items-start gap-4 sm:items-center">
                    <span className="icon-ring size-12 shrink-0 sm:size-14">
                      <group.icon
                        aria-hidden="true"
                        strokeWidth={1.6}
                        className="size-5 sm:size-5.5"
                      />
                    </span>
                    <div>
                      <p className="label-sm text-lime">
                        Clauses {group.range} · {group.eyebrow}
                      </p>
                      <h2
                        id={`${group.id}-title`}
                        className="mt-2 font-display text-[clamp(1.55rem,3vw,2.35rem)] leading-tight font-semibold tracking-[-0.03em] text-platinum"
                      >
                        {group.title}
                      </h2>
                    </div>
                  </div>

                  <ol
                    start={group.clauses[0].number}
                    className="mt-8 flex flex-col gap-5"
                  >
                    {group.clauses.map((clause) => (
                      <li
                        key={clause.number}
                        value={clause.number}
                        data-policy-reveal
                        data-policy-glow
                        className="card-glow relative overflow-hidden rounded-2xl border border-line bg-raised p-6 shadow-card transition duration-500 hover:-translate-y-0.5 hover:border-lime/30 sm:p-8"
                      >
                        <div className="relative z-10 grid gap-5 sm:grid-cols-[52px_minmax(0,1fr)] sm:gap-6">
                          <span
                            aria-hidden="true"
                            className="grid size-11 place-items-center rounded-full border border-lime/35 bg-lime/8 font-display text-[13px] font-bold text-lime tabular-nums sm:size-13"
                          >
                            {String(clause.number).padStart(2, '0')}
                          </span>

                          <div>
                            <h3 className="font-display text-[18px] leading-snug font-semibold tracking-[-0.02em] text-platinum sm:text-[20px]">
                              <span className="sr-only">
                                Clause {clause.number}:{' '}
                              </span>
                              {clause.title}
                            </h3>
                            <div className="mt-5 flex flex-col gap-4 text-[14.5px] leading-[1.75] text-silver sm:text-[15px]">
                              {clause.blocks.map((block, index) => {
                                if (block.kind === 'paragraph') {
                                  return <p key={index}>{block.text}</p>
                                }

                                if (block.kind === 'list') {
                                  return (
                                    <ul
                                      key={index}
                                      className="grid gap-2.5 sm:grid-cols-2"
                                    >
                                      {block.items.map((item) => (
                                        <li
                                          key={item}
                                          className="flex items-start gap-2.5 rounded-xl border border-line-soft bg-elevated px-4 py-3 text-[13.5px] leading-snug text-silver"
                                        >
                                          <BadgeCheck
                                            aria-hidden="true"
                                            strokeWidth={1.8}
                                            className="mt-0.5 size-4 shrink-0 text-lime"
                                          />
                                          {item}
                                        </li>
                                      ))}
                                    </ul>
                                  )
                                }

                                return (
                                  <p
                                    key={index}
                                    className="rounded-xl border border-lime/30 bg-lime/8 px-5 py-4 font-display text-[15px] leading-relaxed font-semibold tracking-[-0.01em] text-platinum sm:text-[16px]"
                                  >
                                    {block.text}
                                  </p>
                                )
                              })}
                            </div>
                          </div>
                        </div>
                      </li>
                    ))}
                  </ol>
                </section>
              ))}
            </div>
          </div>
        </div>
      </Section>

      <Section tone="surface" labelledBy="acknowledgement-title">
        <SectionHeading
          id="acknowledgement-title"
          eyebrow="Acknowledgement"
          title="Customer and showroom record"
          lede="The details and signatures that accompany acceptance of the policy for the relevant vehicle and transaction."
        />

        <div className="mt-12 grid gap-6 lg:grid-cols-2">
          <section
            data-policy-reveal
            aria-labelledby="customer-details-title"
            className="rounded-2xl border border-line bg-raised p-7 shadow-card sm:p-9"
          >
            <div className="flex items-center gap-4">
              <span className="icon-ring size-11">
                <ClipboardCheck aria-hidden="true" strokeWidth={1.6} className="size-4.5" />
              </span>
              <div>
                <p className="label-sm text-lime">To be completed</p>
                <h2
                  id="customer-details-title"
                  className="mt-1.5 font-display text-[20px] font-semibold tracking-[-0.02em] text-platinum"
                >
                  Customer details
                </h2>
              </div>
            </div>

            <dl className="mt-8 grid gap-x-6 gap-y-6 sm:grid-cols-2">
              {CUSTOMER_FIELDS.map((field) => (
                <div key={field}>
                  <dt className="label-sm text-dim">{field}</dt>
                  <dd aria-hidden="true" className="mt-3 h-7 border-b border-line" />
                </div>
              ))}
            </dl>
          </section>

          <section
            data-policy-reveal
            aria-labelledby="sultan-details-title"
            className="rounded-2xl border border-lime/25 bg-raised p-7 shadow-card sm:p-9"
          >
            <div className="flex items-center gap-4">
              <span className="icon-ring size-11">
                <BookOpenCheck aria-hidden="true" strokeWidth={1.6} className="size-4.5" />
              </span>
              <div>
                <p className="label-sm text-lime">For Sultan Motors</p>
                <h2
                  id="sultan-details-title"
                  className="mt-1.5 font-display text-[20px] font-semibold tracking-[-0.02em] text-platinum"
                >
                  Showroom authorisation
                </h2>
              </div>
            </div>

            <dl className="mt-8 grid gap-x-6 gap-y-6 sm:grid-cols-2">
              {SULTAN_FIELDS.map((field) => (
                <div key={field}>
                  <dt className="label-sm text-dim">{field}</dt>
                  <dd aria-hidden="true" className="mt-3 h-7 border-b border-line" />
                </div>
              ))}
            </dl>
          </section>
        </div>

        <div
          data-policy-reveal
          className="mt-12 flex flex-col items-start justify-between gap-7 rounded-2xl border border-line bg-elevated p-7 sm:flex-row sm:items-center sm:p-9"
        >
          <div className="flex max-w-2xl items-start gap-4">
            <span className="icon-ring size-11 shrink-0">
              <WalletCards aria-hidden="true" strokeWidth={1.6} className="size-4.5" />
            </span>
            <div>
              <h2 className="font-display text-[19px] font-semibold tracking-[-0.02em] text-platinum">
                Need a term explained before you sign?
              </h2>
              <p className="mt-2 text-[14px] leading-relaxed text-steel">
                Speak with the Sultan Motors team about the written agreement for your specific vehicle.
              </p>
            </div>
          </div>
          <div className="flex shrink-0 flex-wrap gap-3">
            <Button to="/contact" arrow>
              Contact us
            </Button>
            <Button to="/financing" variant="ghost">
              Financing guide
            </Button>
          </div>
        </div>
      </Section>
    </div>
  )
}

export default Policy
